import { useCallback, useMemo } from 'react';
import {
  type AdminStepScorm,
  useCompleteContentStepScormUploadMutation,
  useDeleteContentStepScormMutation,
  useResumeContentStepScormUploadMutation,
  useStartContentStepScormUploadMutation,
} from '@/entities/Courses';
import { GET_LESSONS, rtkApi } from '@/shared/api/rtkApi';
import { useAppDispatch } from '@/shared/lib/hooks/useAppDispatch';
import type { StepScormUploadSettings, StepScormUploadSubject } from '../model/types';
import { scormUploads } from '../model/scormUploadsStore';
import { isSameScormUploadFile } from './scormState';

const isNotFound = (error: unknown) => (error as { status?: number })?.status === 404;

// Отпечаток файла: по нему бэкенд узнаёт тот же файл, когда загрузку продолжают
const getFileFields = (file: File) => ({
  fileName: file.name,
  fileSize: file.size,
  fileLastModified: file.lastModified,
});

/** Запускает загрузку SCORM-пакета шага через менеджер, связывая его с API шагов */
export const useStepScormUploader = () => {
  const dispatch = useAppDispatch();
  const [startContentStepScormUpload] = useStartContentStepScormUploadMutation();
  const [resumeContentStepScormUpload] = useResumeContentStepScormUploadMutation();
  const [completeContentStepScormUpload] = useCompleteContentStepScormUploadMutation();
  const [deleteContentStepScorm] = useDeleteContentStepScormMutation();

  const start = useCallback(
    // subject — урок и номер шага для трея; settings — настройки балла нового пакета;
    // currentScorm — пакет шага, как его видит карточка: тот же файл продолжает его загрузку
    (
      stepId: number,
      file: File,
      subject: StepScormUploadSubject,
      settings: StepScormUploadSettings,
      currentScorm?: AdminStepScorm | null,
    ) => {
      scormUploads.start(
        stepId,
        file,
        subject,
        {
          start: async (scormFile) => {
            const { data } = await startContentStepScormUpload({
              stepId,
              body: {
                ...getFileFields(scormFile),
                scormHasAssessment: settings.graded,
                ...(settings.graded ? { scormPassingScore: settings.passingScore } : {}),
              },
            }).unwrap();
            return { partSize: data.partSize, parts: data.parts, uploadedPartNumbers: [] };
          },
          resume: async (scormFile) => {
            const { data } = await resumeContentStepScormUpload({
              stepId,
              body: getFileFields(scormFile),
            }).unwrap();
            return {
              partSize: data.partSize,
              parts: data.parts,
              uploadedPartNumbers: data.uploadedPartNumbers,
            };
          },
          complete: async () => {
            await completeContentStepScormUpload(stepId).unwrap();
          },
          removePackage: async () => {
            try {
              await deleteContentStepScorm(stepId).unwrap();
            } catch (error) {
              if (!isNotFound(error)) throw error;
            }
          },
          // Файл ушёл в хранилище напрямую, поэтому урок перечитывается: шаг покажет обработку
          onUploaded: () => {
            dispatch(rtkApi.util.invalidateTags([GET_LESSONS]));
          },
        },
        { resume: isSameScormUploadFile(currentScorm, file) },
      );
    },
    [
      startContentStepScormUpload,
      resumeContentStepScormUpload,
      completeContentStepScormUpload,
      deleteContentStepScorm,
      dispatch,
    ],
  );

  return useMemo(
    () => ({
      start,
      cancel: scormUploads.cancel,
      retry: scormUploads.retry,
      forget: scormUploads.forget,
    }),
    [start],
  );
};
